import type {QuoteFont, QuoteTheme} from '../resolver/types';

/**
 * Every value the two renderers need to draw a theme.
 *
 * Numbers are unitless so the PDF (react-pdf, points) and the preview (CSS px on a 794x1123 sheet)
 * can consume the same token without either converting the other's strings.
 */
export type QuoteThemeTokens = {
	accent: string;
	/**
	 * The heading's LOGICAL face, not a face name. Each renderer maps it itself - the PDF to a
	 * Base-14 PostScript name, the preview to a CSS stack - because a PostScript name like
	 * `Times-Roman` matches no CSS family and would silently fall back to the browser default.
	 */
	headingFont: QuoteFont;
	headingWeight: number;
	headingSize: number;
	headingLetterSpacing: number;
	headingTransform: 'none' | 'uppercase';
	/** Rule under a heading; null when the theme draws none. */
	headingRuleColor: string | null;
	headingPaddingBottom: number;
	ruleColor: string;
	coverHeight: number;
	coverTitleSize: number;
	coverKickerLetterSpacing: number;
	coverBackground: string;
	coverForeground: string;
	coverRuleColor: string | null;
	coverAlign: 'left' | 'center';
	tableHeadBackground: string;
	tableHeadColor: string;
	grandTotalRuleColor: string;
};

/**
 * The design is drawn on an A4 sheet at 96dpi (794x1123); both renderers work in PDF points
 * (595x842). Page-proportional sizes are converted once here, so a value copied off the design
 * lands at the same fraction of the page in the document it is copied into.
 */
const DESIGN_TO_A4 = 842 / 1123;
const scaled = (designPx: number): number => Math.round(designPx * DESIGN_TO_A4 * 10) / 10;

const NEUTRAL_RULE = '#e0e0e8';
const MINIMAL_RULE = '#ececf2';
const MINIMAL_TINT = '#f4f4f7';
const INK = '#232334';

/**
 * The single source of the three themes: one pure function feeding BOTH the HTML preview and the
 * PDF, so the two cannot drift.
 *
 * Accent, logo and wordmark stay constant across all three — a theme changes the cover band, the
 * heading voice and the rule treatment, nothing else.
 */
export function themeTokens(theme: QuoteTheme, accentColor: string, font: QuoteFont): QuoteThemeTokens {
	const base: QuoteThemeTokens = {
		accent: accentColor,
		// Classic is the serif voice, and it resolves to Times: only the Base-14 faces can be
		// name-referenced in a PDF without embedding a font program.
		headingFont: theme === 'classic' ? 'times' : font,
		headingWeight: theme === 'classic' ? 400 : theme === 'minimal' ? 600 : 700,
		headingSize: theme === 'minimal' ? 13 : theme === 'classic' ? 21 : 19,
		headingLetterSpacing: theme === 'minimal' ? 1.6 : theme === 'classic' ? 0 : -0.2,
		headingTransform: theme === 'minimal' ? 'uppercase' : 'none',
		headingRuleColor: theme === 'classic' ? accentColor : theme === 'minimal' ? MINIMAL_RULE : null,
		headingPaddingBottom: theme === 'modern' ? 0 : theme === 'classic' ? 7 : 9,
		ruleColor: theme === 'minimal' ? MINIMAL_RULE : NEUTRAL_RULE,
		coverHeight: scaled(theme === 'modern' ? 392 : theme === 'classic' ? 470 : 286),
		coverTitleSize: scaled(theme === 'minimal' ? 34 : theme === 'classic' ? 52 : 46),
		coverKickerLetterSpacing: scaled(theme === 'minimal' ? 5 : 3),
		coverBackground: theme === 'modern' ? accentColor : theme === 'minimal' ? MINIMAL_TINT : '#ffffff',
		coverForeground: theme === 'modern' ? '#ffffff' : INK,
		coverRuleColor: theme === 'classic' ? accentColor : theme === 'minimal' ? '#e6e6ee' : null,
		coverAlign: theme === 'classic' ? 'center' : 'left',
		tableHeadBackground: accentColor,
		tableHeadColor: '#ffffff',
		grandTotalRuleColor: accentColor,
	};

	// Minimal carries no filled accent blocks — its ink stays in type and rules, which is what makes
	// the three themes read apart at a glance in the preview as well as the PDF.
	if (theme === 'minimal') {
		return {
			...base,
			tableHeadBackground: MINIMAL_TINT,
			tableHeadColor: '#6b6b7b',
			grandTotalRuleColor: INK,
		};
	}
	return base;
}
